import React, { useState, useEffect } from 'react';

const DeliveryCalculator = () => {
  // State variables for user inputs
  const [inStorePrice, setInStorePrice] = useState(14.99);
  const [onlinePrice, setOnlinePrice] = useState(17.24); // 15% above in-store price
  const [merchantFee, setMerchantFee] = useState(0);
  const [isDashPass, setIsDashPass] = useState(false);
  
  // State for input field values (to handle empty fields)
  const [inStorePriceInput, setInStorePriceInput] = useState('14.99');
  const [onlinePriceInput, setOnlinePriceInput] = useState('17.24');
  const [merchantFeeInput, setMerchantFeeInput] = useState('0');
  
  // Fixed commission rate
  const commissionPercent = 15;
  
  // Calculated costs (27% of in-store price)
  const ingredientCost = inStorePrice * 0.27;
  const laborCost = inStorePrice * 0.27;
  
  // State variables for calculation results
  const [commission, setCommission] = useState(0);
  const [effectivePrice, setEffectivePrice] = useState(0);
  const [inStoreMargin, setInStoreMargin] = useState(0);
  const [deliveryMargin, setDeliveryMargin] = useState(0);
  const [inStoreProfit, setInStoreProfit] = useState(0);
  const [deliveryProfit, setDeliveryProfit] = useState(0);
  const [priceDifference, setPriceDifference] = useState(0);
  const [profitAnalysis, setProfitAnalysis] = useState('');
  const [profitPercentage, setProfitPercentage] = useState(0);
  
  // Handle input changes
  const handleInStorePriceChange = (e) => {
    const value = e.target.value;
    setInStorePriceInput(value);
    setInStorePrice(value === '' ? 0 : parseFloat(value));
  };
  
  const handleOnlinePriceChange = (e) => {
    const value = e.target.value;
    setOnlinePriceInput(value);
    setOnlinePrice(value === '' ? 0 : parseFloat(value));
  };
  
  const handleMerchantFeeChange = (e) => {
    const value = e.target.value;
    setMerchantFeeInput(value);
    setMerchantFee(value === '' ? 0 : parseFloat(value));
  };
  
  // Calculate values when inputs change
  useEffect(() => {
    // Use DashPass rate if selected
    const actualCommissionPercent = isDashPass ? 25 : commissionPercent;
    
    // Calculate commission amount
    const commissionAmount = (onlinePrice * (actualCommissionPercent / 100)).toFixed(2);
    setCommission(parseFloat(commissionAmount));
    
    // Calculate effective sales price (online price minus commission AND merchant fee)
    const effectiveSalesPrice = (onlinePrice - commissionAmount - merchantFee).toFixed(2);
    setEffectivePrice(parseFloat(effectiveSalesPrice));
    
    // Calculate contribution margins (revenue minus ingredient costs)
    const storeMargin = (inStorePrice - ingredientCost).toFixed(2);
    setInStoreMargin(parseFloat(storeMargin));
    
    const delMargin = (effectiveSalesPrice - ingredientCost).toFixed(2);
    setDeliveryMargin(parseFloat(delMargin));
    
    // Calculate net profits (revenue minus ingredient AND labor costs)
    const storeProfit = (inStorePrice - ingredientCost - laborCost).toFixed(2);
    setInStoreProfit(parseFloat(storeProfit));
    
    const delProfit = (effectiveSalesPrice - ingredientCost - laborCost).toFixed(2);
    setDeliveryProfit(parseFloat(delProfit));
    
    // Calculate price difference
    const priceDiff = (effectiveSalesPrice - inStorePrice).toFixed(2);
    setPriceDifference(parseFloat(priceDiff));
    
    // Calculate profit percentage of online price
    const profitPercent = ((parseFloat(delProfit) / onlinePrice) * 100).toFixed(1);
    setProfitPercentage(parseFloat(profitPercent));
    
    // Generate profit analysis
    if (parseFloat(delProfit) > (onlinePrice * 0.25)) {
      setProfitAnalysis("Your delivery profit margin is healthy at " + profitPercent + "% of the online price. While it's lower than in-store profit, this remains within a sustainable range for delivery operations. Continue monitoring to ensure profitability is maintained.");
    } else if (parseFloat(delProfit) > 0) {
      setProfitAnalysis("Your delivery orders are profitable but with a slim margin at " + profitPercent + "% of the online price. Consider adjusting your pricing strategy to improve profitability.");
    } else {
      setProfitAnalysis("Your delivery orders are operating at a loss. Consider adjusting your pricing strategy or negotiating better terms with delivery platforms.");
    }
    
  }, [inStorePrice, onlinePrice, merchantFee, isDashPass, ingredientCost, laborCost, commissionPercent]);
  
  return (
    <div className="min-h-full flex flex-col items-center p-6 font-sans bg-gradient-to-b from-white to-[#f8f8fa]">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-lg overflow-hidden border border-[#e6e6e6]/50 transition-all duration-300 hover:shadow-xl">
        {/* Header */}
        <div className="bg-gradient-to-b from-white to-[#f5f5f7] py-8 px-8 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#edbd56]/0 via-[#edbd56] to-[#edbd56]/0"></div>
          <div className="mb-6 relative inline-block">
            <div className="absolute inset-0 bg-[#f8f8fa] rounded-full blur-md opacity-50 transform scale-110"></div>
            <img 
              src="/images/logo.png" 
              alt="Crave Cookies Logo" 
              className="w-36 h-auto relative z-10 transform hover:scale-105 transition-transform duration-300" 
            />
          </div>
          <h1 className="text-2xl font-medium text-[#1d1d1f] mb-2">Delivery Platform Economics</h1>
          <p className="text-[#86868b] text-lg font-light max-w-xs mx-auto">Calculate your true margins and optimize pricing</p>
        </div>
        
        {/* Input Section */}
        <div className="p-8 bg-white">
          <div className="space-y-6">
            <div className="group">
              <label className="block text-sm font-medium text-[#1d1d1f] mb-2 group-hover:text-[#edbd56] transition-colors duration-200">In-Store Price ($)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#86868b]">$</span>
                <input
                  type="text"
                  value={inStorePriceInput}
                  onChange={handleInStorePriceChange}
                  className="w-full pl-8 pr-4 py-3.5 rounded-xl border border-[#e6e6e6] focus:outline-none focus:ring-2 focus:ring-[#edbd56]/50 focus:border-transparent shadow-sm hover:shadow transition-all duration-300"
                />
              </div>
            </div>
            
            <div className="group">
              <label className="block text-sm font-medium text-[#1d1d1f] mb-2 group-hover:text-[#edbd56] transition-colors duration-200">Online Platform Price ($)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#86868b]">$</span>
                <input
                  type="text"
                  value={onlinePriceInput}
                  onChange={handleOnlinePriceChange}
                  className="w-full pl-8 pr-4 py-3.5 rounded-xl border border-[#e6e6e6] focus:outline-none focus:ring-2 focus:ring-[#edbd56]/50 focus:border-transparent shadow-sm hover:shadow transition-all duration-300"
                />
              </div>
            </div>
            
            <div className="group">
              <label className="block text-sm font-medium text-[#1d1d1f] mb-2 group-hover:text-[#edbd56] transition-colors duration-200">
                Merchant/Promo Fee
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#86868b]">$</span>
                <input
                  type="text"
                  value={merchantFeeInput}
                  onChange={handleMerchantFeeChange}
                  className="w-full pl-8 pr-4 py-3.5 rounded-xl border border-[#e6e6e6] focus:outline-none focus:ring-2 focus:ring-[#edbd56]/50 focus:border-transparent shadow-sm hover:shadow transition-all duration-300"
                />
              </div>
              <p className="mt-1.5 text-xs text-[#86868b]">
                Note: This fee is charged only for orders coming from DoorDash/UberEats promotions or ads, not for every purchase.
              </p>
            </div>
            
            <div className="group">
              <label className="block text-sm font-medium text-[#1d1d1f] mb-2 group-hover:text-[#edbd56] transition-colors duration-200">Commission Rate</label>
              <div className="w-full px-4 py-3.5 rounded-xl border border-[#e6e6e6] bg-[#f8f8fa] text-[#1d1d1f] flex justify-between items-center">
                <span>15%</span>
                <span className="text-xs text-[#86868b] bg-white px-2 py-1 rounded-full border border-[#e6e6e6]/50">Crave negotiated rate</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="group">
                <label className="block text-sm font-medium text-[#1d1d1f] mb-2 group-hover:text-[#edbd56] transition-colors duration-200">Ingredient Cost</label>
                <div className="w-full px-4 py-3.5 rounded-xl border border-[#e6e6e6] bg-[#f8f8fa] text-[#1d1d1f]">
                  <div className="flex justify-between items-center">
                    <span>${ingredientCost.toFixed(2)}</span>
                    <span className="text-xs text-[#86868b]">27%</span>
                  </div>
                </div>
              </div>
              
              <div className="group">
                <label className="block text-sm font-medium text-[#1d1d1f] mb-2 group-hover:text-[#edbd56] transition-colors duration-200">Labor Cost</label>
                <div className="w-full px-4 py-3.5 rounded-xl border border-[#e6e6e6] bg-[#f8f8fa] text-[#1d1d1f]">
                  <div className="flex justify-between items-center">
                    <span>${laborCost.toFixed(2)}</span>
                    <span className="text-xs text-[#86868b]">27%</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center pt-2">
              <div className="relative flex items-center">
                <input
                  id="dashpass-checkbox"
                  type="checkbox"
                  checked={isDashPass}
                  onChange={(e) => setIsDashPass(e.target.checked)}
                  className="h-5 w-5 text-[#edbd56] focus:ring-[#edbd56] border-[#e6e6e6] rounded opacity-0 absolute"
                />
                <div className={`h-5 w-5 border rounded flex items-center justify-center ${isDashPass ? 'bg-[#edbd56] border-[#edbd56]' : 'border-[#e6e6e6] bg-white'}`}>
                  {isDashPass && <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>}
                </div>
                <label htmlFor="dashpass-checkbox" className="ml-3 block text-sm text-[#1d1d1f] cursor-pointer">
                  DashPass Order <span className="text-xs text-[#86868b] bg-white px-2 py-0.5 rounded-full border border-[#e6e6e6]/50 ml-1">25% commission</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        
        {/* Results Section */}
        <div className="bg-[#f8f8fa] p-8 border-t border-[#e6e6e6]/30">
          <h2 className="text-xl font-medium text-[#1d1d1f] mb-6">Calculation Results</h2>
          
          <div className="space-y-5">
            <div className="bg-white p-5 rounded-xl border border-[#e6e6e6]/50 shadow-sm">
              <p className="text-sm font-medium text-[#1d1d1f] mb-3 pb-2 border-b border-[#f5f5f7]">Revenue Breakdown</p>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[#86868b]">Online Price:</span>
                  <span className="font-medium text-[#1d1d1f]">${onlinePrice.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-[#86868b]">Commission ({isDashPass ? '25' : '15'}%):</span>
                  <span className="font-medium text-red-600">-${commission.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-[#86868b]">Merchant/Promo Fee:</span>
                  <span className="font-medium text-red-600">-${merchantFee.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center pt-2 mt-2 border-t border-[#f5f5f7]">
                  <span className="text-[#1d1d1f] font-medium">Effective Sales Price:</span>
                  <span className="font-medium text-[#1d1d1f]">${effectivePrice.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-[#86868b]">In-Store Price:</span>
                  <span className="font-medium text-[#1d1d1f]">${inStorePrice.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center pt-2 mt-2 border-t border-[#f5f5f7]">
                  <span className="text-[#1d1d1f] font-medium">Price Difference:</span>
                  <div className={`font-medium px-3 py-1 rounded-full ${priceDifference >= 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                    {priceDifference >= 0 ? '+' : ''}${priceDifference.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-5 rounded-xl border border-[#e6e6e6]/50 shadow-sm">
              <p className="text-sm font-medium text-[#1d1d1f] mb-3 pb-2 border-b border-[#f5f5f7]">Contribution Margin <span className="text-xs text-[#86868b] font-normal">(Revenue - Ingredients)</span></p>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[#86868b]">In-Store:</span>
                  <span className="font-medium text-[#1d1d1f]">${inStoreMargin.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-[#86868b]">Delivery:</span>
                  <span className={`font-medium ${deliveryMargin >= inStoreMargin ? 'text-green-600' : 'text-[#1d1d1f]'}`}>
                    ${deliveryMargin.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-5 rounded-xl border border-[#e6e6e6]/50 shadow-sm">
              <p className="text-sm font-medium text-[#1d1d1f] mb-3 pb-2 border-b border-[#f5f5f7]">Net Profit <span className="text-xs text-[#86868b] font-normal">(Revenue - Ingredients - Labor)</span></p>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[#86868b]">In-Store:</span>
                  <span className={`font-medium ${inStoreProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${inStoreProfit.toFixed(2)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-[#86868b]">Delivery:</span>
                  <span className={`font-medium ${deliveryProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${deliveryProfit.toFixed(2)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center mt-4 pt-3 border-t border-[#f5f5f7]">
                  <span className="text-[#1d1d1f] font-medium">Profitability Difference:</span>
                  <div className={`font-medium px-3 py-1 rounded-full ${deliveryProfit >= inStoreProfit ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                    {deliveryProfit >= inStoreProfit ? '+' : ''}${Math.abs(deliveryProfit - inStoreProfit).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Summary Section */}
            <div className="bg-white p-5 rounded-xl border border-[#e6e6e6]/50 shadow-sm">
              <p className="text-sm font-medium text-[#1d1d1f] mb-3 pb-2 border-b border-[#f5f5f7]">Analysis Summary</p>
              
              <div className="space-y-4">
                <div>
                  <p className="text-[#1d1d1f] font-medium mb-1">In-Store vs. Delivery</p>
                  {priceDifference >= 0 ? (
                    <p className="text-sm text-[#86868b]">
                      Delivery sales generate <span className="text-green-600 font-medium">${priceDifference.toFixed(2)}</span> more revenue per order compared to in-store sales.
                    </p>
                  ) : (
                    <p className="text-sm text-[#86868b]">
                      Delivery sales generate <span className="text-red-600 font-medium">${Math.abs(priceDifference).toFixed(2)}</span> less revenue per order compared to in-store sales.
                    </p>
                  )}
                </div>
                
                <div>
                  <p className="text-[#1d1d1f] font-medium mb-1">Profit Analysis</p>
                  <p className={`text-sm ${deliveryProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {profitAnalysis}
                  </p>
                </div>
                
                <div>
                  <p className="text-[#1d1d1f] font-medium mb-1">Recommendation</p>
                  {deliveryProfit > inStoreProfit ? (
                    <p className="text-sm text-[#86868b]">
                      Delivery orders are <span className="text-green-600 font-medium">more profitable</span> than in-store orders. Consider promoting delivery options.
                    </p>
                  ) : deliveryProfit > 0 ? (
                    <p className="text-sm text-[#86868b]">
                      In-store orders are <span className="text-green-600 font-medium">more profitable</span> than delivery orders, but delivery still contributes positively to your bottom line.
                    </p>
                  ) : (
                    <p className="text-sm text-[#86868b]">
                      Delivery orders are operating at a <span className="text-red-600 font-medium">loss</span>. Consider adjusting your pricing strategy or negotiating better terms with delivery platforms.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Summary Box */}
        <div className="p-8 bg-gradient-to-b from-[#edbd56]/10 to-[#edbd56]/5 border-t border-[#edbd56]/20">
          <div className="flex items-start">
            <div className="bg-white p-2 rounded-full border border-[#edbd56]/20 mr-4 mt-1">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#edbd56]" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium text-[#1d1d1f] mb-2">Analysis Summary</h3>
              <p className="text-sm leading-relaxed text-[#1d1d1f]/80">
                {deliveryProfit > (onlinePrice * 0.25) ? (
                  `Your delivery profit margin is healthy at ${(deliveryProfit / onlinePrice * 100).toFixed(1)}% of the online price. While it's lower than in-store profit, this remains within a sustainable range for delivery operations.`
                ) : deliveryProfit > 0 ? (
                  `Your in-store net profit is $${(inStoreProfit - deliveryProfit).toFixed(2)} higher than delivery, 
                  suggesting you may need to adjust your online pricing strategy.`
                ) : (
                  `Your delivery orders are operating at a loss. Consider adjusting your online pricing or negotiating better terms with delivery platforms.`
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-8 text-sm text-[#86868b] max-w-md text-center">
        <p className="leading-relaxed">
          This calculator helps you understand how delivery platforms affect your margins.
          It demonstrates that evaluating only by commission percentage can be misleading.
        </p>
        <p className="mt-2 text-xs text-[#86868b]/70">
          &copy;2025 Crave Cookies Franchising, LLC
        </p>
      </div>
    </div>
  );
};

export default DeliveryCalculator;
